import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toaster } from 'evergreen-ui';

import { Page } from '../common';
import { useTheme } from '../../hooks';
import { checkAccountStatus } from '../../actions/stripe';
import { Loader, Heading, Container, Text, Button } from '../../styleguide';
import { TextWrapper } from '../../styles/shop';
import { ButtonsWrapper } from '../../styles/form';
import { SPACING } from '../../styles/constants';

const StripeAccountOnboarding = () => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [accountStatus, setAccountStatus] = React.useState(null);

  // Extract shop ID from URL
  const urlParams = new URLSearchParams(location.search);
  const shopId = urlParams.get('shop');
  const isRefresh = urlParams.get('refresh') === 'true';

  React.useEffect(() => {
    if (!shopId) {
      setError('ID du shop manquant');
      setLoading(false);
      return;
    }

    const checkAccount = async () => {
      try {
        const status = await checkAccountStatus(shopId);
        setAccountStatus(status);

        if (status.onboardingComplete) {
          // Onboarding complete, redirect to dashboard
          toaster.success('Compte Stripe créé avec succès !');
          history.push(`/shopDashboard/${shopId}`);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkAccount();
  }, [shopId, isRefresh, history]);

  const handleContinueOnboarding = () => {
    if (accountStatus?.accountLinkUrl) {
      window.location.replace(accountStatus.accountLinkUrl);
    }
  };

  const handleReturnHome = () => {
    history.push('/');
  };

  if (loading) {
    return (
      <Page name="Vérification du compte Stripe">
        <Container>
          <Loader
            color={theme.primaryColor}
            background={theme.backgroundColor}
          />
          <TextWrapper withTopMargin>
            <Text size={500} muted styles={{ marginTop: 8 }}>
              Vérification du statut de votre compte Stripe...
            </Text>
          </TextWrapper>
        </Container>
      </Page>
    );
  }

  if (error) {
    return (
      <Page name="Erreur - Compte Stripe">
        <Container>
          <Heading size={900} centered>
            Erreur lors de la vérification
          </Heading>
          <TextWrapper withTopMargin>
            <Text size={500} muted styles={{ marginTop: 8 }}>
              {error}
            </Text>
          </TextWrapper>
          <ButtonsWrapper>
            <Button appearance="primary" onClick={handleReturnHome}>
              Retour à l'accueil
            </Button>
          </ButtonsWrapper>
        </Container>
      </Page>
    );
  }

  if (accountStatus?.onboardingComplete) {
    // This condition should not occur as we already redirect,
    // but included for safety
    return (
      <Page name="Compte Stripe - Succès">
        <Container>
          <Heading size={900} centered>
            Compte créé avec succès !
          </Heading>
          <TextWrapper withTopMargin>
            <Text size={500} muted styles={{ marginTop: 8 }}>
              Votre compte Stripe est maintenant actif. Redirection en cours...
            </Text>
          </TextWrapper>
        </Container>
      </Page>
    );
  }

  if (accountStatus?.needsOnboarding) {
    return (
      <Page name="Finalisation - Compte Stripe">
        <Container>
          <Heading size={900} centered>
            Finalisation de votre compte Stripe
          </Heading>

          {isRefresh && (
            <TextWrapper withTopMargin>
              <Text size={500} muted styles={{ marginTop: 8 }}>
                Il semble que votre inscription n'a pas été finalisée. Vous
                pouvez continuer là où vous vous êtes arrêté.
              </Text>
            </TextWrapper>
          )}

          <TextWrapper withTopMargin>
            <Text size={400} muted>
              Pour finaliser votre inscription et commencer à recevoir des
              paiements, vous devez compléter la configuration de votre compte
              Stripe.
            </Text>
          </TextWrapper>

          {accountStatus.requirements &&
            accountStatus.requirements.currently_due.length > 0 && (
              <TextWrapper>
                <Text size={400} muted>
                  Informations requises :{' '}
                  {accountStatus.requirements.currently_due.join(', ')}
                </Text>
              </TextWrapper>
            )}

          <ButtonsWrapper
            overrides={`
              gap: ${SPACING[200]};
            `}
          >
            <Button
              primary
              onClick={handleContinueOnboarding}
              disabled={!accountStatus.accountLinkUrl}
            >
              Terminer onboarding
            </Button>
            <Button
              minimal
              variant="outlined"
              onClick={handleReturnHome}
              style={{ marginLeft: '10px' }}
            >
              Plus tard
            </Button>
          </ButtonsWrapper>
        </Container>
      </Page>
    );
  }

  return (
    <Page name="Compte Stripe">
      <Container>
        <Heading size={900} centered>
          Configuration du compte Stripe
        </Heading>
        <TextWrapper withTopMargin>
          <Text size={500} muted styles={{ marginTop: 8 }}>
            Aucun compte Stripe trouvé. Veuillez créer un nouveau compte.
          </Text>
        </TextWrapper>
        <ButtonsWrapper>
          <Button primary onClick={handleReturnHome}>
            Retour à l'accueil
          </Button>
        </ButtonsWrapper>
      </Container>
    </Page>
  );
};

export default StripeAccountOnboarding;
